import { clsx } from 'clsx';
import { KinematicBodyFacet } from 'engine/facets/KinematicBodyFacet';
import { FILTER_GLOW_BEIGE_ID } from 'game/defs/FilterGlowBeige';
import { createKeyframes, createStyles } from 'game/lib/createStyles';
import { ModuleType } from 'game/state/BotState';
import { memo, useRef } from 'react';
import { ModuleComponentProps } from './ModuleProps';

const pulse = createKeyframes('pulse', {
  '0%': { opacity: 0.5 },
  '50%': { opacity: 1 },
  '100%': { opacity: 0.5 },
});

const classes = createStyles({
  light: {
    transition: 'opacity 200ms',
    opacity: 0,
  },
  running: {
    animation: `${pulse} 1500ms infinite`,
  },
  complete: {
    opacity: 1,
  },
});

const DoorModuleStatic: React.FC = memo(() => {
  return (
    <>
      <defs>
        <image
          id="image"
          width="28"
          height="28"
          xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAYAAAByDd+UAAAACXBIWXMAAAsSAAALEgHS3X78AAAEXUlEQVRIS62WvW4bRxCAv9lb/ki2LMZEAhtEbCaAnNLq0rqI8giuVLp0oVKP4NKFS5es/AhJCiOdO8FNbBgBaCHMDxxZlKKI5N3tTordvTvSVhIHGWB4R3J3vpnZ2dkVVeXfyO5guA3cAXorf02Bp6PJ+OCdSe8R+Tvg7mDYA/ZA75+Vrj91C/XqBYmTgYxMN21bLmXZEfAY5MFoMp5eZPNC4O5guAf64JfFvH24+EMKdWSiGAk8IX2AqtCSjEF7Qz9pd3KQ/dFk/PB9dt8Bhqj0yXFR7Lw4P6ZUhzWQiWCEoMgKEBTwChmWz7qbbFr7Lcjd1WiXgBH27Of5bOvlbCrWgBWwImQJCnWUDaBH8RqgpYebnZ5+3O68AvmyCTUsiT6ZzGdbL84bMCO0jNCSoG0jtA3xGf+L31tGsAZaGRwuTuRNvtgCfdIkVMDdwXDvqCh2fjg/FmuULEZmJRix0XArGm7H92VwcMqKYDPl9WIqp2W5E+ohiKhqSuWv3x//1nG4erIhGIiGMhOcCOtYpzSl0qlSKpSqlF4pPIhabl/uL0CujSbjaYxQ93+az9q5OoxAVqmQiWCFCmblgshMcKxacxEyAUfJm3zRBvaaKb334+xURGJBpOIggE0DnKKtUhvX1SaHjCw5bAQm+ZmA3gewu4Ph9klZ9ufe0Tah0lMlGglpqwyYsD6txjZRBZcKXUKl1nMVI5D7kj+d6+8OhtsGuPO2mKsQZgnV9kKQas81I85EqrSmykyZaDYGk+yJclrmCtwxQK9QLwmW5H3vEiM2UW1cp2C4BjWdTvMdToCe5UNE4yaPT4fiw88oSrNnrb7PvQfAAkgcnAbpkiqekHAfNZV/WrsyOuFRfMNW02bHGIChTYD09NVTY3+UuMfAqGI8iBHUKyaN1bD3XBznVPGqVSY8lYwtMLWSqVckDEgDBaehGNKmNhpWRn2ovnRO+QpWQ+uIA7SNVWBqgaf9Vlc8J7EBNyMKRgQQDWQVUEkNPKWvBpVel8Dp/ZJtCfDUjCbjg01rjzomCyCaA5XS162q9FCoUnglX1IofPi9bMxxPsAsGZez7Gg0GR/ETiOPbq1dUd8AOSUWhuISKMGUClY0NDlVagQrOK982tlQ4DHUre3hjbX1vC1ZnZroYaEBVnpiQ17WGlw7lcBOlUws1zrdHORBBQwHpOxvb1zFVemgMbk2XnjIl0Cr4OBkqUrhhVtrPQhXjimweuLf/OZwdv7V87NjSf0ynBRSty6IFVqLUpd/VbEebq1/pNc73e9Gk9dfp7ErnUbu3lhbfwZsPT87FjVxL3rwqaURWxxBk7te055UnApfrPf0eqf7CuTuEuGiS9Tveb5zcPaW3Lulo0YiMCx+QIatEaJsieX25T4bNvvnS1RT0jXxcHbefjU7lYV3KxHWSVWgazI+717RQXftw66JTYnR7gP3Tsqyf1rmOvOlNNevJZlebXXkirVHII+Ah//pIrwq/9dV/y/YGAlu4EikMAAAAABJRU5ErkJggg=="
        />
      </defs>
      <path fill="#f2f2f2" d="M0.5 0.5H399.5V599.5H0.5z" />
      <path fill="#4d4d4d" d="M399 1v598H1V1h398m1-1H0v600h400V0z" />
      <g opacity="0.8">
        <circle cx="34.75" cy="35.75" r="14.75" fill="#fff" />
        <circle
          cx="34.75"
          cy="35.75"
          r="14.75"
          fill="none"
          stroke="#fff"
        />
      </g>
      <circle cx="34.75" cy="34.75" r="14.75" fill="#ccc" />
      <circle
        cx="34.75"
        cy="34.75"
        r="14.75"
        fill="none"
        stroke="gray"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M29.5 30.5L39.5 40.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M29.5 29.5L39.5 39.5"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M39.5 30.5L29.5 40.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M39.5 29.5L29.5 39.5"
      />
      <g opacity="0.8">
        <circle cx="364.75" cy="35.75" r="14.75" fill="#fff" />
        <circle
          cx="364.75"
          cy="35.75"
          r="14.75"
          fill="none"
          stroke="#fff"
        />
      </g>
      <circle cx="364.75" cy="34.75" r="14.75" fill="#ccc" />
      <circle
        cx="364.75"
        cy="34.75"
        r="14.75"
        fill="none"
        stroke="gray"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M359.5 30.5L369.5 40.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M359.5 29.5L369.5 39.5"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M369.5 30.5L359.5 40.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M369.5 29.5L359.5 39.5"
      />
      <g opacity="0.8">
        <circle cx="34.75" cy="565.75" r="14.75" fill="#fff" />
        <circle
          cx="34.75"
          cy="565.75"
          r="14.75"
          fill="none"
          stroke="#fff"
        />
      </g>
      <circle cx="34.75" cy="564.75" r="14.75" fill="#ccc" />
      <circle
        cx="34.75"
        cy="564.75"
        r="14.75"
        fill="none"
        stroke="gray"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M29.5 560.5L39.5 570.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M29.5 559.5L39.5 569.5"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M39.5 560.5L29.5 570.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M39.5 559.5L29.5 569.5"
      />
      <g opacity="0.8">
        <circle cx="364.75" cy="565.75" r="14.75" fill="#fff" />
        <circle
          cx="364.75"
          cy="565.75"
          r="14.75"
          fill="none"
          stroke="#fff"
        />
      </g>
      <circle cx="364.75" cy="564.75" r="14.75" fill="#ccc" />
      <circle
        cx="364.75"
        cy="564.75"
        r="14.75"
        fill="none"
        stroke="gray"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M359.5 560.5L369.5 570.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M359.5 559.5L369.5 569.5"
      />
      <path
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        d="M369.5 560.5L359.5 570.5"
        opacity="0.8"
      />
      <path
        fill="none"
        stroke="#333"
        strokeWidth="2"
        d="M369.5 559.5L359.5 569.5"
      />
      <circle cx="330" cy="520" r="19.5" fill="#ff931e" />
      <path
        fill="#f15a24"
        d="M330 501a19 19 0 11-19 19 19 19 0 0119-19m0-1a20 20 0 1020 20 20 20 0 00-20-20z"
      />
      <g fill="#fff" opacity="0.75">
        <circle cx="330" cy="521" r="9.5" />
        <path d="M330 512a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z" />
      </g>
      <circle cx="330" cy="520" r="9.5" />
      <path
        fill="#f15a24"
        d="M330 511a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
      />
      <text
        fontSize="27.77"
        transform="translate(292.6 490.14)"
      >
        OPEN
      </text>
      <path d="M380 520L360 510 360 530 380 520z" />
      <text
        fontSize="27.77"
        transform="translate(39.29 490.14)"
      >
        PWR
      </text>
      <path d="M40 520.35L20 510.35 20 530.35 40 520.35z" />
      <circle cx="70" cy="520" r="19.5" fill="#ff1d25" />
      <path
        fill="#c1272d"
        d="M70 501a19 19 0 11-19 19 19 19 0 0119-19m0-1a20 20 0 1020 20 20 20 0 00-20-20z"
      />
      <g fill="#fff" opacity="0.75">
        <circle cx="70" cy="521" r="9.5" />
        <path d="M70 512a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z" />
      </g>
      <circle cx="70" cy="520" r="9.5" />
      <path
        fill="#c1272d"
        d="M70 511a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
      />
      <path
        fill="#ff1d25"
        d="M400 195.39L400 142.9 217.39 164.82 0 164.82 0 179.47 217.39 179.47 400 195.39z"
        opacity="0.8"
      />
      <path
        fill="#ff931e"
        d="M230.94 200.75L400 250.55v-49.13l-182.61-18.29H0v14.64h203.13a130.11 130.11 0 0127.81 2.98z"
        opacity="0.8"
      />
      <path
        fill="#3fa9f5"
        d="M286.27 128.86a16 16 0 01-.62 2h.62z"
        opacity="0.8"
      />
      <path
        fill="#3fa9f5"
        d="M400 136.4V84l-97.61 33.16-2.56 7.22-3.36-1.31c-1-.36-1.74-.64-2.35-.8a4.16 4.16 0 00-.89-.17 2.36 2.36 0 00-.08.71v8H296v10.44h-21.65v-7.85a16.93 16.93 0 01-2.94 3.61 20.47 20.47 0 01-26.82 0c-.07-.07-.13-.14-.2-.2l-4.48 1.52a137.64 137.64 0 01-48.34 8.52H0v14.27h217.39z"
        opacity="0.8"
      />
      <path
        fill="#bcacac"
        d="M275.92 310.85a20.07 20.07 0 00-21.35 6.45 20.49 20.49 0 01-15.77 7.7h-77.6a20.49 20.49 0 01-15.77-7.7 20.07 20.07 0 00-21.35-6.45A19.32 19.32 0 00111 323.61a20 20 0 0034.44 19.09 20.47 20.47 0 0115.76-7.7h77.6a20.47 20.47 0 0115.77 7.7A20 20 0 00289 323.61a19.32 19.32 0 00-13.08-12.76z"
      />

      <path d="M162.4 262.24a12.8 12.8 0 016.44 1.7l-2.09 4.25a9 9 0 00-4-1.15c-1.25 0-1.92.41-1.92 1.27 0 .41.31.89 2.23 1.61 2.57 1 6.19 2.69 6.19 6.26 0 5-3.84 6.6-7.77 6.6a12.34 12.34 0 01-7.68-2.28l2.61-4.48a10.67 10.67 0 005.16 1.87c1.27 0 1.88-.46 1.88-1.39 0-1.2-1.54-1.9-4.09-3.05-2.28-1-3.72-2.93-3.72-4.95.04-3.84 3.5-6.26 6.76-6.26z" />
      <path d="M182.78 262.6v4.63h-4.32v15.17h-5.28v-15.17h-4.32v-4.63zM194 262.6l8.07 19.8h-5.69l-1.2-3.29h-7.25l-1.2 3.29h-5.69l8.07-19.8zm-.6 11.88l-1.84-4.71-1.85 4.71zM214.27 262.6v4.63H210v15.17h-5.28v-15.17h-4.32v-4.63zM231.41 262.6v12.82c0 4.89-2.79 7.36-8 7.36s-8-2.47-8-7.36V262.6h5.28v11.23c0 3.07.82 3.77 2.71 3.77s2.71-.7 2.71-3.77V262.6zM240.74 262.24a12.74 12.74 0 016.43 1.7l-2.09 4.25a8.94 8.94 0 00-4-1.15c-1.24 0-1.92.41-1.92 1.27 0 .41.32.89 2.24 1.61 2.56 1 6.19 2.69 6.19 6.26 0 5-3.84 6.6-7.78 6.6a12.37 12.37 0 01-7.68-2.28l2.62-4.48a10.67 10.67 0 005.16 1.87c1.27 0 1.87-.46 1.87-1.39 0-1.2-1.54-1.9-4.08-3.05-2.28-1-3.72-2.93-3.72-4.95.02-3.84 3.5-6.26 6.76-6.26z" />

      <use transform="translate(256 316)" xlinkHref="#image" />
      <path
        fill="none"
        stroke="#000"
        d="M260 280v-20a10 10 0 00-10-10H150a10 10 0 00-10 10v20a10 10 0 01-10 10H50a10 10 0 00-10 10v60a10 10 0 0010 10h300a10 10 0 0010-10v-60a10 10 0 00-10-10h-80a10 10 0 01-10-10z"
      />
      <path d="M34 100h10.05l11.64 33.52h2v4.22h-17v-4.22h3.22l-1.62-5H29.42l-1.71 5H31v4.22H20v-4.22h2.44zm2 9.55l-4.81 14h9.47zM71.16 109.78h.41v18.67a4.28 4.28 0 00.69 2.65 2.44 2.44 0 002.07.89 4.93 4.93 0 002-.48 6.53 6.53 0 001.92-1.36v-14.39l-1.86.33V113l11.41-3.22h.33v24.54h1.95v3.44H79l-1.16-3.33a18.72 18.72 0 01-4.32 3.06 9.34 9.34 0 01-4.1 1 7.41 7.41 0 01-5.7-2.2 8.7 8.7 0 01-2.06-6.19v-14.34l-1.89.33V113zM104.68 103.61l2.79.63v6.6h4.81l-.56 3.19a2.5 2.5 0 01-.27 1 .75.75 0 01-.55.17h-3.43v14a5.9 5.9 0 00.54 2.95 2 2 0 001.84.86 3.15 3.15 0 001-.19 6.15 6.15 0 001.23-.59l1.44 2.11a15 15 0 01-4.28 3.14 10.49 10.49 0 01-4.54 1.08q-3.8 0-5.5-1.8t-1.7-5.86v-15.71H95v-3.14a16 16 0 006.1-3.19 12.57 12.57 0 003.58-5.25zM130.68 109.78a15.41 15.41 0 0111 4.09 14.36 14.36 0 010 20.6 17 17 0 01-22.05 0 14.36 14.36 0 010-20.63 15.59 15.59 0 0111.05-4.06zm5.19 17.83a20 20 0 00-2.2-9.58q-2.21-4.11-4.78-4.11a2.81 2.81 0 00-2.56 1.68 10 10 0 00-.92 4.76 21.34 21.34 0 002.13 9.86c1.43 2.77 3 4.15 4.74 4.15a2.91 2.91 0 002.68-1.71 11.05 11.05 0 00.91-5.05zM151.13 120.09h14.37v7.63h-14.37zM170 100h15.23q9.56 0 14.91 4.91t5.35 13.54a21.47 21.47 0 01-1.65 8.72 17.75 17.75 0 01-5.11 6.59 16.16 16.16 0 01-5.83 3.06 33.19 33.19 0 01-8.72.92H170v-4.22h2.7v-29.23H170zm14.23 5.06v27.7h1.16q3.7 0 5.76-3.74t2.06-10.57q0-6.72-2.06-10a6.73 6.73 0 00-6.14-3.34zM224.18 109.78a15.42 15.42 0 0111 4.09 14.35 14.35 0 010 20.6 17 17 0 01-22 0 14.36 14.36 0 010-20.63 15.58 15.58 0 0111-4.06zm5.19 17.83a20 20 0 00-2.2-9.58q-2.21-4.11-4.78-4.11a2.83 2.83 0 00-2.57 1.68 10.07 10.07 0 00-.92 4.76 21.22 21.22 0 002.14 9.86q2.13 4.16 4.73 4.15a2.89 2.89 0 002.68-1.71 11 11 0 00.92-5.05zM258 109.78a15.41 15.41 0 0111 4.09 14.36 14.36 0 010 20.6 17 17 0 01-22 0 14.36 14.36 0 010-20.63 15.59 15.59 0 0111-4.06zm5.2 17.83A20 20 0 00261 118q-2.2-4.11-4.77-4.11a2.83 2.83 0 00-2.57 1.68 10 10 0 00-.92 4.76 21.34 21.34 0 002.13 9.86q2.14 4.16 4.74 4.15a2.91 2.91 0 002.68-1.71 11 11 0 00.93-5.02zM298.06 109.51l2.95 1-3.27 9.25c-1.07-.41-2-.72-2.7-.92a7.11 7.11 0 00-1.85-.29 3.23 3.23 0 00-2.6 1.12 4.66 4.66 0 00-.94 3.09v11.53h2.81v3.44h-14.61v-3.44h1.92v-13.88a3.68 3.68 0 00-.19-1.46 1.86 1.86 0 00-.7-.7l-1 .84-2-2.22 6-7.09a15.6 15.6 0 014 2.52 7.78 7.78 0 012.1 3.08 25.61 25.61 0 013.5-4.6 3.68 3.68 0 012.42-1 6.35 6.35 0 011.64.26 13.75 13.75 0 012 .74z" />
      <path
        fill="#5b5555"
        d="M326.86 323a5 5 0 119.95 0v3a2 2 0 004 0v-3a9 9 0 10-17.93 0v3h-13.95a4 4 0 00-4 4v11.95a4 4 0 004 4h19.92a4 4 0 004-4v-12.03a4 4 0 00-4-4h-2zM76.7 323.11v3h10.09v-3a5 5 0 00-10.09 0zm-4 3v-3a9.08 9.08 0 0118.16 0v3h1a4 4 0 014 4v12.1a4 4 0 01-4 4h-20.2a4 4 0 01-4-4v-12.1a4 4 0 014-4z"
      />
      <use transform="translate(116 316)" xlinkHref="#image" />
    </>
  );
});

export const DoorModuleComponent: React.FC<ModuleComponentProps<ModuleType.Door>> = ({ bot }) => {
  const body = bot.getFacet(KinematicBodyFacet)!;

  const [x] = bot.transform.position;
  const [min, max] = [
    body.initialPosition[0] + body.props.constrainX![0],
    body.initialPosition[0] + body.props.constrainX![1],
  ];
  const complete = x === min || x === max;

  const directionRef = useRef<'open' | 'close'>(x === max ? 'open' : 'close');
  const lastXRef = useRef(x);
  if (lastXRef.current < x) {
    directionRef.current = 'open';
  } else if (lastXRef.current > x) {
    directionRef.current = 'close';
  }

  const classMap = {
    [directionRef.current]: complete ? classes.complete : classes.running,
  };

  lastXRef.current = x;
  return (
    <>
      <DoorModuleStatic />
      <circle
        filter={`url(#${FILTER_GLOW_BEIGE_ID})`}
        cx="130"
        cy="330"
        r="13"
        fill="#fff"
        className={clsx(classes.light, classMap.close)}
      />
      <circle
        filter={`url(#${FILTER_GLOW_BEIGE_ID})`}
        cx="270"
        cy="330"
        r="13"
        fill="#fff"
        className={clsx(classes.light, classMap.open)}
      />
    </>
  );
};
